/**
 * Soft warning when formal Diagnostic Decision disagrees with the provisional
 * ADHD PAD outcome chosen at the end of CI (SLaM Path 1 — PAD in appointment).
 *
 * Product rule (v1): both stand. DD is the formal record; the in-clinic PAD is
 * not auto-switched. This dialog is informational only — save already proceeds.
 * Visual language mirrors SSIDdConsistencyDialog (amber alert shell) without
 * the two hard forks that dialog requires.
 */

import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { ExclamationIcon } from '@heroicons/react/outline';

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {'positive'|'negative'|null} props.provisionalAdhdOutcome
 * @param {string} props.diagnosisDecision - Formal Fully met / Partially met / Not met
 * @param {function} props.onAcknowledge - Dismiss; clinician stays on DD
 */
export default function PadDdMismatchDialog({
  isOpen,
  provisionalAdhdOutcome,
  diagnosisDecision = '',
  onAcknowledge,
}) {
  const provisionalLabel =
    provisionalAdhdOutcome === 'positive'
      ? 'positive (criteria appear met)'
      : provisionalAdhdOutcome === 'negative'
        ? 'negative (criteria do not appear met)'
        : 'your in-clinic choice';

  return (
    <AlertDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onAcknowledge?.();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto shadow-xl text-left">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <ExclamationIcon className="w-7 h-7 text-amber-600" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <AlertDialog.Title className="text-xl font-bold text-gray-900 text-left">
                Formal decision differs from in-clinic PAD
              </AlertDialog.Title>
              <AlertDialog.Description className="text-sm text-gray-600 mt-1 text-left">
                You selected <strong>{diagnosisDecision || 'this ADHD decision'}</strong>,
                but the Post-Assessment Discussion started in clinic used a{' '}
                <strong>{provisionalLabel}</strong> discussion.
              </AlertDialog.Description>
            </div>
          </div>

          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-left text-sm text-amber-900">
            <p>
              Your Diagnostic Decision is the formal record and has been saved.
              The in-clinic Post-Assessment Discussion is not changed automatically.
            </p>
            <p className="mt-2">
              Because you discussed a different outcome with the patient, consider
              scheduling a follow-up appointment to share the updated conclusion.
              The patient's overview will flag this Post-Assessment Discussion as
              potentially out of date until then.
            </p>
          </div>

          <div className="flex justify-end border-t border-gray-200 pt-4">
            <AlertDialog.Action asChild>
              <button
                type="button"
                onClick={() => onAcknowledge?.()}
                className="px-4 py-2 text-sm font-semibold text-white bg-violet-700 rounded-md hover:bg-violet-800 transition-colors"
              >
                Continue
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/**
 * Returns true when Path 1 provisional PAD outcome disagrees with the formal
 * Fully/Partially vs Not met decision — used to decide whether to open the soft dialog.
 *
 * @param {Object} args
 * @param {string|null} args.ciFinishPath
 * @param {'positive'|'negative'|null|undefined} args.provisionalAdhdOutcome
 * @param {string} args.diagnosisDecision
 */
export function shouldShowPadDdMismatch({
  ciFinishPath,
  provisionalAdhdOutcome,
  diagnosisDecision,
}) {
  if (ciFinishPath !== 'padInAppointment') return false;
  if (provisionalAdhdOutcome !== 'positive' && provisionalAdhdOutcome !== 'negative') {
    return false;
  }
  const formalPositive =
    diagnosisDecision === 'Fully met' || diagnosisDecision === 'Partially met';
  const formalNegative = diagnosisDecision === 'Not met';
  if (!formalPositive && !formalNegative) return false;
  const provisionalPositive = provisionalAdhdOutcome === 'positive';
  return formalPositive !== provisionalPositive;
}
